import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import Breadcrumb from '@/components/common/Breadcrumb';
import CommonTable, {
  Action,
  TableColumn,
} from '@/components/common/CommonTable';
import ImagePreview from '@/components/common/media/ImagePreview';
import StatusDropdown from '@/components/common/StatusComponent';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import DetailsCard from '@/components/UI/DetailsCard';
import {
  ICollection,
  ICollectionDetailProp,
  ICollectionProductResponse,
} from '@/interfaces/collection.interface';
import {
  checkIsExpire,
  showToastNotification,
  toFixedThree,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { Star } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const CollectionDetails: React.FC = () => {
  const navigate = useNavigate();
  const [states, setStates] = useState<ICollectionDetailProp>({
    isLoading: false,
    collectInfo: null,
    produacts: [],
  });
  const { id } = useParams();

  const breadcrumb = [
    { label: 'Collection', href: RoutesLink.collection },
    { label: 'Details' },
  ];

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  useEffect(() => {
    fetchCollectionInfo();
  }, []);

  const fetchCollectionInfo = async () => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.bundles}/${id}`;
      const res = await callApi<ICollection>(PATH, { method: 'GET' });
      handleChangeState({
        produacts: res?.items,
        collectInfo: res,
        isLoading: false,
      });
    } catch (error: unknown) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      console.log('error in fetching product info', error);
    }
  };

  const handleDeleteProduct = async (id: string) => {
    try {
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}/${id}`;
      await callApi(PATH, { method: 'DELETE' });
      fetchCollectionInfo();
      showToastNotification('success', 'Product deleted successfully');
    } catch (error: unknown) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      console.log('error', error);
    } finally {
      handleChangeState({ isLoading: false });
    }
  };

  const columns: TableColumn<ICollectionProductResponse>[] = [
    {
      key: 'product',
      header: 'Product',
      render: (item) => {
        const url = item?.product?.directImageUrl
          ? item?.product?.directImageUrl
          : item?.product?.media?.[0]?.url;
        return (
          <ImagePreview
            src={`${API_URL_PRODUCT_SERVICE}/${url}`}
            alt={''}
            text={item?.product?.name}
          />
        );
      },
      className: 'p-3',
    },
    {
      key: 'price',
      header: 'Price (BHD)',
      render: (item) => {
        const price =
          item?.product?.productInfo?.[0]?.secondaryPrice ||
          item?.product?.productInfo?.[0]?.price ||
          0;
        return <span>{toFixedThree(price)}</span>;
      },
    },
    {
      key: 'stock',
      header: 'Stock',
      render: (item) => item?.product.productInfo[0]?.secondaryQuantity || 0,
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => (
        <StatusDropdown
          value={item?.product?.status}
          options={[]}
          onChange={() => {}}
          canEditVal="product"
        />
      ),
    },
    {
      key: 'discount',
      header: 'Discount',
      render: (item) => (
        <span>
          {checkIsExpire(item.product?.productInfo[0]?.discountEndDate)
            ? 'Yes'
            : 'No'}
        </span>
      ),
    },
    {
      key: 'averageRating',
      header: 'Rating',
      render: (item) => (
        <div className="p-3 flex items-center gap-1">
          {toFixedThree(item.product.averageRating || 0, 1)}{' '}
          <Star size={16} className="text-orange-400 fill-orange-500" />
        </div>
      ),
    },
  ];

  const actions: Action<ICollectionProductResponse>[] = [
    {
      text: 'View',
      onClick: (item) =>
        navigate(`${RoutesLink.productDetail}/${item.product.id}`),
    },
    {
      text: 'Edit',
      onClick: (item) =>
        navigate(`${RoutesLink.addProducts}/${item.product.id}`),
    },
    {
      text: 'Delete',
      onClick: (item) => handleDeleteProduct(item.product.id),
    },
  ];

  const collectionDetails = [
    { label: 'Title', value: states.collectInfo?.title },
    { label: 'Price', value: states.collectInfo?.price },
    { label: 'Products', value: states.collectInfo?.items?.length },
    { label: 'Description', value: states.collectInfo?.description },
  ];

  return (
    <Layout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <Breadcrumb items={breadcrumb} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
          <DetailsCard title="Collection Info" details={collectionDetails} />
        </div>
        <Card>
          <CommonTable
            columns={columns}
            data={states?.produacts}
            showCheckbox={false}
            emptyMessage="No products found"
            loading={states.isLoading}
            actions={actions}
          />
        </Card>
      </div>
    </Layout>
  );
};

export default CollectionDetails;
